/**
 * Post Content Module
 * - Generates Table of Contents from headings (h2–h6) in rich text
 * - Wraps sections hierarchically and adds IDs for anchor links
 * - Keeps hierarchy: e.g. H3 and its content are inside the H2 section
 * - TOC links use custom scroll with 20vh top offset (Lenis-compatible)
 * - Active section detection: is-in-view on the current TOC link
 */

import gsap from "@/lib/gsap";
import { Scroll } from "@/lib/scroll";
import { onDestroy } from "@/modules/_";
import { prefersReducedMotion } from "@/utils/media";

const HEADING_SELECTOR = "h2, h3, h4, h5, h6";
/** Top offset for scroll target and "in view" line (vh). */
const SCROLL_OFFSET_VH = 20;
/** Viewport max-width (px) below which the TOC dropdown is active. */
const TOC_DROPDOWN_BREAKPOINT = 767;
const HEADING_LEVEL_MAP = { H2: 2, H3: 3, H4: 4, H5: 5, H6: 6 };

/** Debounce helper for resize. */
function debounce(fn, ms) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), ms);
  };
}

/** Run DOM update; use View Transitions API when available to reduce perceived layout shift. */
function withViewTransition(update) {
  if (typeof document.startViewTransition === "function") {
    document.startViewTransition(update);
  } else {
    update();
  }
}

/**
 * Generate a URL-safe slug from heading text. Ensures uniqueness via usedSlugs Set.
 * @param {string} text
 * @param {Set<string>} usedSlugs
 * @returns {string}
 */
function slugify(text, usedSlugs = new Set()) {
  const base = text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\u00C0-\u024F\-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "section";
  let slug = base;
  let n = 0;
  while (usedSlugs.has(slug)) {
    n += 1;
    slug = `${base}-${n}`;
  }
  usedSlugs.add(slug);
  return slug;
}

/**
 * Collect all headings from content element with level, text, slug and element.
 * @param {Element} contentEl
 * @returns {{ element: Element, level: number, text: string, slug: string }[]}
 */
function collectHeadings(contentEl) {
  const headings = contentEl.querySelectorAll(HEADING_SELECTOR);
  const usedSlugs = new Set();
  return Array.from(headings).map((el) => {
    const level = HEADING_LEVEL_MAP[el.tagName];
    const text = el.textContent?.trim() ?? "";
    const slug = slugify(text, usedSlugs);
    return { element: el, level, text, slug };
  });
}

/**
 * Build a nested TOC tree from flat heading list.
 * @param {{ level: number, text: string, slug: string }[]} headings
 * @returns {{ level: number, text: string, slug: string, children: unknown[] }[]}
 */
function buildTocTree(headings) {
  const root = [];
  const stack = [{ level: 0, children: root }];
  for (const h of headings) {
    const node = { level: h.level, text: h.text, slug: h.slug, children: [] };
    while (stack.length > 1 && stack[stack.length - 1].level >= h.level) {
      stack.pop();
    }
    stack[stack.length - 1].children.push(node);
    stack.push(node);
  }
  return root;
}

/**
 * Render TOC tree to DOM (ol/li with links).
 * @param {{ level: number, text: string, slug: string, children: unknown[] }[]} tree
 * @param {string} linkClass
 * @param {string} listClass
 * @param {string} itemClass
 * @returns {HTMLUListElement}
 */
function renderTocList(tree, linkClass, listClass, itemClass) {
  const ol = document.createElement("ol");
  ol.setAttribute("role", "list");
  ol.className = listClass;
  for (const node of tree) {
    const li = document.createElement("li");
    li.className = itemClass;
    const a = document.createElement("a");
    a.href = `#${node.slug}`;
    a.className = linkClass;
    a.setAttribute("tabindex", "0");
    const label = document.createElement("div");
    label.textContent = node.text;
    a.appendChild(label);
    li.appendChild(a);
    if (node.children && node.children.length > 0) {
      li.appendChild(renderTocList(node.children, linkClass, listClass, itemClass));
    }
    ol.appendChild(li);
  }
  return ol;
}

/**
 * Wrap a range of sibling nodes (from start to before end) in a section with id.
 * @param {Element} parent
 * @param {Node} startNode - first node to include (inclusive)
 * @param {Node | null} endNode - first node to exclude (exclusive); null = until end
 * @param {string} sectionId
 */
function wrapRangeInSection(parent, startNode, endNode, sectionId) {
  const section = document.createElement("section");
  section.id = sectionId;
  const nodesToMove = [];
  let n = startNode;
  while (n && n !== endNode) {
    nodesToMove.push(n);
    n = n.nextSibling;
  }
  parent.insertBefore(section, startNode);
  for (const node of nodesToMove) {
    section.appendChild(node);
  }
}

/**
 * Find next sibling that is a heading with level <= given level (or end of parent).
 * @param {Node} startAfter
 * @param {{ element: Element, level: number }[]} headings
 * @param {number} maxLevel
 * @returns {Node | null}
 */
function findNextHeadingSibling(startAfter, headings, maxLevel) {
  let n = startAfter.nextSibling;
  while (n) {
    const idx = headings.findIndex((x) => x.element === n);
    if (idx !== -1 && headings[idx].level <= maxLevel) return n;
    n = n.nextSibling;
  }
  return null;
}

/**
 * Apply hierarchical section wrapping: from level 6 down to 2, wrap each heading
 * and its content (until next same-or-higher-level heading) in a <section id="slug">.
 * Keeps e.g. H3 and its content inside the H2 section.
 * @param {{ element: Element, level: number, slug: string }[]} headings
 * @param {Element} contentEl
 */
function wrapSections(headings, contentEl) {
  if (headings.length === 0) return;
  const byLevel = /** @type {Map<number, { element: Element, level: number, slug: string }[]>} */ (new Map());
  for (const h of headings) {
    if (!byLevel.has(h.level)) byLevel.set(h.level, []);
    byLevel.get(h.level).push(h);
  }
  // Process from innermost (H6) to outermost (H2) so nested sections stay inside parent
  for (let level = 6; level >= 2; level--) {
    const list = byLevel.get(level);
    if (!list) continue;
    for (const h of list) {
      const startNode = h.element;
      const endNode = findNextHeadingSibling(startNode, headings, level);
      const parent = startNode.parentNode;
      if (!parent) continue;
      wrapRangeInSection(parent, startNode, endNode, h.slug);
    }
  }
}

/**
 * Build share URL for a given platform and current page URL.
 * @param {string} target - data-target value: whatsapp, linkedin, facebook, x-twitter
 * @param {string} url - page URL to share
 * @returns {string|null} share URL or null if unknown target
 */
function getShareUrl(target, url) {
  const encoded = encodeURIComponent(url);
  switch (target) {
    case "whatsapp":
      return `https://wa.me/?text=${encoded}`;
    case "linkedin":
      return `https://www.linkedin.com/shareArticle?mini=true&url=${encoded}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encoded}`;
    case "x-twitter":
      return `https://twitter.com/intent/tweet?url=${encoded}`;
    default:
      return null;
  }
}

/**
 * Initialize post-content: build TOC, add heading IDs, wrap sections, share buttons.
 * @param {Element} element - [data-module="post-content"]
 * @param {DOMStringMap} [dataset]
 */
export default function (element, dataset) {
  const navEl = element.querySelector('[data-element="navigation"]');
  const contentEl =
    element.querySelector("article") ||
    element.querySelector('[data-element="content"]');
  if (!contentEl) {
    console.warn("post-content: article or [data-element=\"content\"] not found");
    return;
  }

  const headings = collectHeadings(contentEl);

  // --- Social Share: one delegated handler for all .ressources-post-content_share_link[data-target] ---
  function handleShareClick(e) {
    const link = e.target.closest(".ressources-post-content_share_link[data-target]");
    if (!link) return;
    const target = link.getAttribute("data-target");
    if (!target) return;
    const shareUrl = getShareUrl(target, window.location.href);
    if (!shareUrl) return;
    e.preventDefault();
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  }
  element.addEventListener("click", handleShareClick);
  onDestroy(() => element.removeEventListener("click", handleShareClick));

  if (headings.length === 0) return;

  // Add ids to heading elements (for direct link targets)
  for (const h of headings) {
    h.element.id = h.slug;
  }

  // Wrap sections hierarchically (H6 → H2 so nesting is correct)
  wrapSections(headings, contentEl);

  // Build and inject TOC if navigation container exists
  if (navEl) {

    const listClass = "ressources-post-content_navigation_list";
    const itemClass = "ressources-post-content_navigation_list_item";
    const linkClass = "ressources-post-content_navigation_link w-inline-block";
    const tree = buildTocTree(headings);
    const tocList = renderTocList(tree, linkClass, listClass, itemClass);


    withViewTransition(() => {
      const existingList = navEl.querySelector(`.${listClass}`);
      if (existingList) existingList.replaceWith(tocList);
      else navEl.appendChild(tocList);
    });

    element.setAttribute("data-initialized", "true");

    // Map slug -> TOC link (built from tocList we created, so we know the structure)
    const tocLinksBySlug = new Map();
    tocList.querySelectorAll('a[href^="#"]').forEach((a) => {
      const slug = a.getAttribute("href")?.slice(1);
      if (slug) tocLinksBySlug.set(slug, a);
    });

    // Sections for active detection (use section element with id = slug)
    const sectionElements = headings
      .map((h) => document.getElementById(h.slug))
      .filter((el) => el);

    const activeSlugs = new Set();

    function syncIsInViewToLinks() {
      tocLinksBySlug.forEach((link, slug) => {
        link.classList.toggle("is-in-view", activeSlugs.has(slug));
      });
    }

    // Intersection Observer: "in view" = section contains the line at 20vh from top (rootMargin: px only, no vh)
    const topPx = Math.round((SCROLL_OFFSET_VH / 100) * window.innerHeight);
    const bottomPx = Math.round(((100 - SCROLL_OFFSET_VH - 1) / 100) * window.innerHeight);
    const observerRootMargin = `-${topPx}px 0px -${bottomPx}px 0px`;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const slug = entry.target.id;
          if (!slug) continue;
          if (entry.isIntersecting) activeSlugs.add(slug);
          else activeSlugs.delete(slug);
        }
        syncIsInViewToLinks();
      },
      {
        root: null,
        rootMargin: observerRootMargin,
        threshold: 0,
      }
    );
    sectionElements.forEach((el) => observer.observe(el));
    syncIsInViewToLinks();

    function scrollToSection(slug) {
      const section = document.getElementById(slug);
      if (!section) return;
      const offsetPx = (SCROLL_OFFSET_VH / 100) * window.innerHeight;
      const rect = section.getBoundingClientRect();
      const targetScroll = rect.top + (Scroll?.scroll ?? window.scrollY) - offsetPx;
      Scroll.scrollTo(Math.max(0, targetScroll), { immediate: false });
    }

    function handleTocClick(e) {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const slug = a.getAttribute("href")?.slice(1);
      if (!slug) return;
      const section = document.getElementById(slug);
      if (!section) return;
      e.preventDefault();
      e.stopPropagation(); // prevent Lenis anchor handler from scrolling to element (top-aligned)
      history.replaceState(null, "", `#${slug}`);
      scrollToSection(slug);
    }

    navEl.addEventListener("click", handleTocClick);

    // Scroll to URL hash if it targets a section we just generated
    const hashSlug = window.location.hash.slice(1);
    if (hashSlug && tocLinksBySlug.has(hashSlug)) {
      requestAnimationFrame(() => {
        scrollToSection(hashSlug);
      });
    }

    // --- Dropdown (mobile ≤767px): GSAP open/close + debounced resize ---
    const triggerEl = navEl.querySelector(
      '[data-element="toc-trigger"]',
    ) || navEl.querySelector(".ressources-post-content_navigation_trigger");
    const dropdownEl = navEl.querySelector(
      '[data-element="toc-dropdown"]',
    ) || navEl.querySelector(".ressources-post-content_navigation_dropdown");

    if (triggerEl && dropdownEl) {
      const dropdownId =
        dropdownEl.id || `post-content-toc-dropdown-${Math.random().toString(36).slice(2, 9)}`;
      if (!dropdownEl.id) dropdownEl.id = dropdownId;

      const tocDropdownMedia = window.matchMedia(
        `(max-width: ${TOC_DROPDOWN_BREAKPOINT}px)`,
      );
      const prefersReduced = prefersReducedMotion();
      const dropdownDuration = prefersReduced ? 0 : 0.25;
      const dropdownEase = "power2.out";
      let dropdownTween = null;

      function isDropdownOpen() {
        return triggerEl.getAttribute("aria-expanded") === "true";
      }

      function openDropdown() {
        if (dropdownTween) dropdownTween.kill();
        dropdownEl.removeAttribute("aria-hidden");
        triggerEl.setAttribute("aria-expanded", "true");
        dropdownEl.classList.add("is-open");

        const height = dropdownEl.scrollHeight;
        dropdownTween = gsap.to(dropdownEl, {
          height: "auto",
          opacity: 1,
          duration: dropdownDuration,
          ease: dropdownEase,
          onComplete: () => {
            dropdownTween = null;
          },
        });
      }

      function closeDropdown() {
        if (dropdownTween) dropdownTween.kill();
        dropdownTween = gsap.to(dropdownEl, {
          height: 0,
          opacity: 0,
          duration: dropdownDuration,
          ease: dropdownEase,
          onComplete: () => {
            if (dropdownEl.contains(document.activeElement)) {
              triggerEl.focus();
            }
            triggerEl.setAttribute("aria-expanded", "false");
            dropdownEl.classList.remove("is-open");
            if (tocDropdownMedia.matches) {
              dropdownEl.setAttribute("aria-hidden", "true");
            } else {
              dropdownEl.removeAttribute("aria-hidden");
            }
            dropdownTween = null;
          },
        });
      }

      function syncDropdownToViewport() {
        if (!tocDropdownMedia.matches) {
          if (dropdownEl.contains(document.activeElement)) {
            triggerEl.focus();
          }
          triggerEl.setAttribute("aria-expanded", "false");
          dropdownEl.classList.remove("is-open");
          dropdownEl.removeAttribute("aria-hidden");
          if (dropdownTween) {
            dropdownTween.kill();
            dropdownTween = null;
          }
          gsap.set(dropdownEl, { clearProps: "height,opacity,overflow" });
        }
      }

      const handleResize = debounce(syncDropdownToViewport, 150);

      function toggleDropdown() {
        if (!tocDropdownMedia.matches) return;
        if (isDropdownOpen()) closeDropdown();
        else openDropdown();
      }

      triggerEl.setAttribute("aria-controls", dropdownId);
      if (!triggerEl.id) {
        triggerEl.id = `post-content-toc-trigger-${dropdownId.split("-").pop()}`;
      }
      dropdownEl.setAttribute("aria-labelledby", triggerEl.id);
      if (!tocDropdownMedia.matches) {
        triggerEl.setAttribute("aria-expanded", "false");
        dropdownEl.classList.remove("is-open");
        dropdownEl.removeAttribute("aria-hidden");
      } else if (!isDropdownOpen()) {
        dropdownEl.setAttribute("aria-hidden", "true");
      }

      function handleCloseDropdownOnLinkClick(e) {
        if (e.target.closest('a[href^="#"]') && tocDropdownMedia.matches) {
          closeDropdown();
        }
      }

      triggerEl.addEventListener("click", toggleDropdown);
      tocDropdownMedia.addEventListener("change", (e) => {
        if (!e.matches) closeDropdown();
      });
      window.addEventListener("resize", handleResize);
      navEl.addEventListener("click", handleCloseDropdownOnLinkClick);

      onDestroy(() => {
        triggerEl.removeEventListener("click", toggleDropdown);
        window.removeEventListener("resize", handleResize);
        navEl.removeEventListener("click", handleCloseDropdownOnLinkClick);
        if (dropdownTween) dropdownTween.kill();
      });
    }

    onDestroy(() => {
      navEl.removeEventListener("click", handleTocClick);
      observer.disconnect();
      tocLinksBySlug.forEach((link) => link.classList.remove("is-in-view"));
    });
  }
}
